import { useCallback, useContext, useEffect, useState } from 'react';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { IPostSendMessageApiResponse } from '../../interfaces/api-interfaces-miaw';
import { IApiHooksWithTriggerData, IIdentityObject } from '../../interfaces/app-interfaces';
import { IMessage } from '../../interfaces/component-interfaces';
import { sendTextMessage } from '../../utils/miaw/api-utils';
import { util } from '../../utils/miaw/common';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { getRoutingAttributes } from '../../utils/miaw/start-chat-utils';
import { useCustomChatEvents } from '../custom-hooks/useCustomChatEvents';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { getQueue } from '../../utils/miaw/init-chat-utils';
import { useIdentity } from '@vp/auth-react';
import { getIsSendRoutingAttributes, setIsSendRoutingAttributes } from '../../utils/session-storage-utils';

const useMIAWChatMessage = (): IApiHooksWithTriggerData<IMessage, string> => {
  const { sendChatEvents } = useCustomChatEvents();
  const identity = useIdentity();

  const {
    chatState: { userInfo, customQueue },
  } = useContext(CAContext);
  const {
    setState,
    state: { conversationId, sendRoutingDataWithMessage, queueInfo },
  } = useContext(MiawChatContext);

  const {
    state: { sessionId, orderNumber, selectedProductIds },
  } = useContext(ChatbotContext);

  const {
    state: { queueType, caseData },
  } = useContext(CaseDataContext);

  const { logCustomAttributesNR } = useNRLogging();

  const [data, setData] = useState<IMessage>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<string>();
  const trigger = useCallback((userMessage: string) => {
    setRequestData(userMessage);
  }, []);

  useEffect(() => {
    if (requestData) {
      setLoading(true);
      if (conversationId && caseData) {
        const { canonicalId } = identity as IIdentityObject;

        /** either from context or from localstorage */
        const sendInfo = sendRoutingDataWithMessage || getIsSendRoutingAttributes();

        logCustomAttributesNR('ca-chatbot-miaw-api-request', {
          logLevel: 'info',
          message: 'Sending user message',
          conversationId,
          apiType: 'miawSendMessage',
        });

        logCustomAttributesNR('ca-chatbot-miaw-api-request', {
          logLevel: 'info',
          message: `Logging user email exists: ${userInfo?.userData?.email ? 'yes' : 'no'}  and lastName exists: ${userInfo?.userData?.lastName ? 'yes' : 'no'}`,
          conversationId,
          apiType: 'miawSendMessage',
        });

        /** Send Routing info with message if agent has ended the chat */
        sendTextMessage(
          conversationId,
          requestData,
          util.generateUUID(),
          undefined,
          sendInfo,
          sendInfo
            ? getRoutingAttributes(
                {
                  chatbotIdentifier: sessionId,
                  orderNumber,
                  selectedProductIds,
                  userShopperId: canonicalId,
                  isLoggedIn: userInfo?.isLoggedIn?.toString() || '',
                },
                caseData, // Pass caseData to getRoutingAttributes
                getQueue(queueType, queueInfo),
                customQueue || queueType,
                userInfo?.userData,
              )
            : undefined,
        )
          .then((res: IPostSendMessageApiResponse) => {
            console.log('Message sent successfully:', res);
            setState((prev) => ({
              ...prev,
              sendRoutingDataWithMessage: false, // Reset the flag after sending the message
            }));
            // Clear localstorage once this is used
            setIsSendRoutingAttributes(false);
            setData({
              id: res.conversationEntries[0].id,
              messageContent: requestData,
              senderDisplayName: 'User',
              timestamp: res.conversationEntries[0].clientTimestamp,
              type: 'user',
              showFeedback: false,
            });
            setError(undefined);
            sendChatEvents('onChatMessage', {});
            logCustomAttributesNR('ca-chatbot-miaw-api-response', {
              logLevel: 'info',
              message: 'User message sent successfully',
              conversationId,
              apiType: 'miawSendMessage',
            });
          })
          .catch((error: Error) => {
            setError(error);
            if (error instanceof Response) {
              error
                .text()
                .then((errorMessage: string) => {
                  const err = new Error(errorMessage);
                  logCustomAttributesNR('ca-chatbot-miaw-api-error', {
                    errorName: err.name,
                    errorStack: err.stack,
                    message: err.message,
                    logLevel: 'error',
                    apiType: 'miawSendMessage',
                  });
                })
                .catch((textError) => {
                  console.error(textError);
                });
            } else {
              logCustomAttributesNR('ca-chatbot-miaw-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                logLevel: 'error',
                apiType: 'miawSendMessage',
              });
            }
          })
          .finally(() => {
            setLoading(false);
            setRequestData(undefined); // Reset requestData after completion
          });
      }
    }
  }, [conversationId, requestData, userInfo]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};
export { useMIAWChatMessage };
